import React, { useEffect, useRef, useState } from 'react';
import Button from '../../components/Button/Button';
import { AiOutlineLoading } from "react-icons/ai";
import { courseList } from '../../utils/constant';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { errorToast } from '../../utils/errorHandling';
import { getExperienceById, postExperienceById, updateExperienceById } from '../../services/experience';
import { Divider, Select, Space } from 'antd';
import AutoShrinkText from '../../utils/AutoShrinkText';
import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import utc from "dayjs/plugin/utc";
import { getCourses } from '../../services/settings';

const base_url = process.env.REACT_APP_BASE_URL;

const ViewExperience = () => {
    const { t } = useTranslation();
    const [courseListData, setCourseListData] = useState({});
    const [oriCourseListData, setOriCourseListData] = useState(null);
    const [courseData, setCourseData] = useState(
        localStorage.getItem("courseData")
            ? JSON.parse(localStorage.getItem("courseData"))
            : null
    );
    const [fetchExperienceData, setFetchExperienceData] = useState(null);
    const languageSelected = localStorage.getItem('i18nextLng');
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const openDropdownsRef = useRef(0); // Track the number of open dropdowns
    const showLoading = isLoading;
    const showError = !oriCourseListData && !fetchExperienceData && !isLoading;
    const showForm = oriCourseListData && fetchExperienceData;
    const showCTAOnly = oriCourseListData && !fetchExperienceData && !isLoading;
    dayjs.extend(utc);

    const fetchExperience = async () => {
        try {
            const data = await getExperienceById();
            if (data) {
                courseList(t).map((i) => {
                    handleExperienceChange(data, i.category);
                });
                setFetchExperienceData(data);
            }
        } catch (error) {
            errorToast(error, t("general.formNotFound"), t);
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        if (!oriCourseListData) return;
        fetchExperience();
    }, [oriCourseListData]);

    const getAllCourse = async () => {
        try {
            const res = await getCourses();
            if (res) {
                const now = new Date();
                const filteredRes = res.filter(item => {
                    const startDate = new Date(item.start_datetime);
                    return startDate < now;
                });
                const grouped = filteredRes.reduce((acc, item) => {
                    var category = item.course_category;

                    if (typeof category === 'string' && category.includes('online')) {
                        category = 'online_course';
                    }

                    if (!acc[category]) {
                        acc[category] = [];
                    }

                    const newItem = {
                        id: item.id,
                        course_cname: item.course_cname,
                        course_ename: item.course_ename,
                        start_datetime: item.start_datetime,
                        end_datetime: item.end_datetime,
                        value: item.id,
                        label: languageSelected === "en" ? item.course_ename : item.course_cname,
                        participated: 0
                    };

                    acc[category].push(newItem);

                    return acc;
                }, {});
                setCourseListData(grouped);
                setOriCourseListData(grouped);
            }
        } catch (error) {
            errorToast(error, t('general.ActionFailed'), t);
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isDropdownOpen) {
            document.body.style.overflow = 'hidden'; // Disable body scroll
        } else {
            document.body.style.overflow = 'auto';  // Re-enable body scroll
        }
    }, [isDropdownOpen]);

    const handleDropdownVisibleChange = (open) => {
        // Update the ref count
        if (open) {
            openDropdownsRef.current += 1; // Increment when a dropdown opens
        } else {
            openDropdownsRef.current -= 1; // Decrement when a dropdown closes
        }

        // Update the state based on the ref count
        setIsDropdownOpen(openDropdownsRef.current > 0);
    };

    const handleCancel = () => {
        courseList(t).map((i) => {
            handleExperienceChange(fetchExperienceData, i.category);
        });
        setIsEditing(false);
    };

    useEffect(() => {
        getAllCourse();
    }, []);

    const getSelectedCourses = (courseData) => {
        return (courseData ?? [])
            .filter(item => item.participated === "1")
            .map(item => ({
                value: item.value,
                label: languageSelected === "en"
                    ? item.course_ename
                    : item.course_cname
            }));
    };

    const handleExperienceChange = (selectedIds, category) => {
        setCourseListData((prevState) => ({
            ...prevState,
            [category]: oriCourseListData[category].map(item => ({
                ...item,
                participated: selectedIds.includes(item.value) ? "1" : "0"
            }))
        }));
    }
    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const response = await postExperienceById(courseListData);
            if (response) {
                toast.success(t("Experience.submitSuccess"));
            }
        } catch (error) {
            errorToast(error, t("general.failSubmit"), t);
        } finally {
            setIsSubmitting(false);
            setIsEditing(false);
        }
    };

    const ExperienceHeader = () => (
        <>
            <h3>{t("Experience.retreat_experience")}</h3>
            <p>
                {languageSelected === "en"
                    ? courseData.course_ename
                    : courseData.course_cname}
            </p>
        </>
    );

    return (
        <>
            {
                showLoading &&
                <div className='info-container'>
                    <ExperienceHeader />
                    <AiOutlineLoading className='spin' />
                    <p>{t("Experience.loading")}</p>
                </div>

            }
            {showError &&
                <div className='info-container'>
                    <ExperienceHeader />
                    <p style={{ "color": "#ff0000" }}>{t("personal_info.msg_error")}<br /> {t("personal_info.msg_admin")}</p>
                    <div className='action-buttons'>
                        <Link to={`${process.env.REACT_APP_CUSTOMER_SERVICE_LINK}${JSON.parse(localStorage.getItem("token"))}`} target="_blank" rel="noopener noreferrer">
                            <Button
                                buttonStyle='btn--primary'
                                buttonSize='btn--small'
                            >{t("personal_info.contact_admin")}</Button>
                        </Link>
                    </div>
                </div>
            }
            {
                showForm &&
                <>
                    <div className='info-container'>
                        <ExperienceHeader />
                        {!isEditing ?
                            <div className='action-buttons'>
                                <Button
                                    buttonStyle='btn--primary'
                                    buttonSize='btn--small'
                                    onClick={() => setIsEditing(true)}
                                >{t("general.BTNedit")}</Button>
                            </div>
                            :
                            <div className='action-buttons'>
                                <Button buttonStyle='btn--primary' disabled={isSubmitting} onClick={handleCancel} type="button">{t("general.BTNcancel")}</Button>
                            </div>
                        }
                    </div>
                    <div className='form-wrapper'>
                        <form>
                            <h5 className='mt-3'>{t("Experience.instruction")}</h5>
                            {
                                courseList(t).map((i, index) => {
                                    return (
                                        <>
                                            <div className="form-item" key={index}>
                                                <label>{i.label}</label>
                                            </div>
                                            <Select
                                                // key={getSelectedCourses(courseListData[i.category])}
                                                disabled={!isEditing}
                                                mode="multiple"
                                                style={{
                                                    width: '100%',
                                                }}
                                                dropdownStyle={{ maxHeight: 300, overflowY: "auto" }}
                                                size='large'
                                                value={getSelectedCourses(courseListData[i.category])}
                                                showSearch={false}
                                                // onDropdownVisibleChange={handleDropdownVisibleChange}
                                                placeholder={t("Experience.instruction")}
                                                onChange={(e) => handleExperienceChange(e, i.category)}
                                                options={courseListData[i.category]}
                                                optionRender={(option) =>
                                                (
                                                    <Space direction='vertical'>
                                                        <AutoShrinkText maxFont={16} minFont={12}>
                                                            <span style={{ color: "#e67300" }}>
                                                                {languageSelected === "en" ? option.data.course_ename : option.data.course_cname}
                                                            </span>
                                                        </AutoShrinkText>
                                                        <span style={{ color: "#575655" }}>
                                                            {
                                                                i.category !== "international_english_course" ?
                                                                    localStorage.getItem("i18nextLng") === "en" ?
                                                                        dayjs.utc(option.data.start_datetime).format("D-")
                                                                        +
                                                                        dayjs.utc(option.data.end_datetime).format("D MMMM YYYY")
                                                                        :
                                                                        dayjs.utc(option.data.start_datetime)
                                                                            .locale("zh-cn")
                                                                            .format("YYYY年M月D日 - ")
                                                                        +
                                                                        dayjs.utc(option.data.end_datetime)
                                                                            .locale("zh-cn")
                                                                            .format("M月D日")
                                                                    :
                                                                    ""
                                                            }
                                                        </span>
                                                        <Divider style={{ margin: '4px 0', color: '#000000' }} />
                                                    </Space>
                                                )
                                                }
                                            />
                                        </>
                                    )
                                })
                            }
                            <div className='submit-button d-flex justify-content-center mt-4 gap-3'>
                                <Button buttonStyle='btn--primary' disabled={isSubmitting} type="button" onClick={handleSubmit}>
                                    {isSubmitting ? <AiOutlineLoading className="spin" /> : t("general.BTNsave")}
                                </Button>
                            </div>
                        </form>
                    </div>
                </>
            }
            {
                showCTAOnly &&
                <div className='info-container'>
                    <ExperienceHeader />
                    <p style={{ "color": "#ff0000" }}><br />{t("Experience.submitInstruction")}</p>
                    <div className='action-buttons'>
                        <Link to={`${base_url}/retreat_experience`}>
                            <Button
                                buttonStyle='btn--primary'
                                buttonSize='btn--small'
                            >{t("Experience.submitButton")}</Button>
                        </Link>

                    </div>
                </div>
            }
        </>
    )
}

export default ViewExperience